import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Stack, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import useApiRequest from "../../hooks/useApiRequest";

const schema = yup.object().shape({
    name: yup
        .string()
        .required("El nombre es obligatorio")
        .max(255, "Máximo 255 caracteres")
        .matches(
            /^[a-zA-ZáéíóúÁÉÍÓÚñÑ ]*$/,
            "Solo se permiten letras y espacios"
        ),

    surname: yup
        .string()
        .required("El apellido es obligatorio")
        .max(255, "Máximo 255 caracteres")
        .matches(
            /^[a-zA-ZáéíóúÁÉÍÓÚñÑ ]*$/,
            "Solo se permiten letras y espacios"
        ),

    email: yup
        .string()
        .required("El email es obligatorio")
        .email("Debe ser un email válido")
        .max(255, "Máximo 255 caracteres"),

    client_code: yup
        .string()
        .required("El código de cliente es obligatorio")
        .max(255, "Máximo 255 caracteres"),

    password: yup.string().when("$isNew", {
        is: true,
        then: (schema) =>
            schema
                .required("La contraseña es obligatoria")
                .min(8, "Debe tener al menos 8 caracteres")
                .max(255, "Máximo 255 caracteres")
                .matches(/[a-z]/, "Debe contener al menos una letra minúscula")
                .matches(/[A-Z]/, "Debe contener al menos una letra mayúscula")
                .matches(/\d/, "Debe contener al menos un número")
                .matches(
                    /[@$!%*?&]/,
                    "Debe contener al menos uno de los siguientes caracteres especiales: @$!%*?&"
                ),
        otherwise: (schema) => schema.notRequired(),
    }),

    confirmPassword: yup.string().when(["password", "$isNew"], {
        is: (password, isNew) => isNew && !!password,
        then: (schema) =>
            schema
                .required("Debes repetir la contraseña")
                .oneOf([yup.ref("password")], "Las contraseñas no coinciden"),
    }),
});

const UserForm = ({ isNew, refreshData, setIsEditing, initialUserData }) => {
    const navigate = useNavigate();
    const { update, create } = useApiRequest(`/users`, !isNew);

    const handleSave = async (data) => {
        try {
            let result;
            if (isNew) {
                result = await create(data);
            } else {
                result = await update(initialUserData.id, data);
            }

            if (result.success) {
                // Refrescar datos y cerrar el formulario de edición
                refreshData();
                setIsEditing(false);
                if (isNew && result.data?.id) {
                    navigate("/user-form", { state: { id: result.data?.id } });
                }
            }
        } catch (error) {
            console.error("Error al guardar cambios:", error);
        }
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: initialUserData,
        context: { isNew },
    });

    const onSubmit = (data) => {
        handleSave(data);
    };

    return (
        <Stack direction="column" spacing={2} minWidth={"300px"}>
            <TextField
                label="Nombre"
                {...register("name")}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                error={!!errors.name}
                helperText={errors.name?.message}
                variant="outlined"
            />
            <TextField
                label="Apellido"
                {...register("surname")}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                error={!!errors.surname}
                helperText={errors.surname?.message}
                variant="outlined"
            />
            <TextField
                label="Email"
                {...register("email")}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                error={!!errors.email}
                helperText={errors.email?.message}
                variant="outlined"
            />
            <TextField
                label="Código de cliente"
                {...register("client_code")}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                error={!!errors.client_code}
                helperText={errors.client_code?.message}
                variant="outlined"
            />
            {isNew && (
                <>
                    <TextField
                        label="Contraseña"
                        {...register("password")}
                        slotProps={{ htmlInput: { maxLength: 255 } }}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        variant="outlined"
                    />
                    <TextField
                        label="Repetir contraseña"
                        type="password"
                        {...register("confirmPassword")}
                        slotProps={{ htmlInput: { maxLength: 255 } }}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        variant="outlined"
                    />
                </>
            )}
            <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmit)}
            >
                {isNew ? "Crear" : "Guardar"}
            </Button>
        </Stack>
    );
};

export default UserForm;
